// src/utils/bookingEngine.js
// LOOP R0.2 — Booking Lifecycle Core
// Small, centralized helpers for canonical reservation writes.
// Goal: keep create/edit/status/payment/schedule paths aligned without doing a risky calendar rewrite.

import {
  bookingLifecycleFromRecord,
  buildBookingLifecyclePatch,
  lifecycleNumber,
  normalizeBookingStatus,
  normalizePaymentStatus,
} from "./bookingLifecycle";

function clean(value, fallback = "") {
  const out = String(value ?? "").trim();
  return out || fallback;
}

function safeNumber(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function firstNonEmpty(...values) {
  for (const value of values) {
    if (value !== undefined && value !== null && String(value).trim() !== "") return value;
  }
  return null;
}

function safeDate(value) {
  try {
    if (!value) return null;
    if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
    if (typeof value.toDate === "function") return value.toDate();
    if (value.seconds) return new Date(Number(value.seconds) * 1000);
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  } catch (_) {
    return null;
  }
}

function dateInputValue(date) {
  const d = safeDate(date);
  if (!d) return null;
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function timeInputValue(date) {
  const d = safeDate(date);
  if (!d) return null;
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

export function resolveBookingCustomerIdentity(raw = {}) {
  const customerId = clean(firstNonEmpty(
    raw.customerId,
    raw.clientId,
    raw.customerDocId,
    raw.customer?.id,
    raw.customerSnapshot?.id,
    raw.customerSnapshot?.customerId
  ));

  const name = clean(firstNonEmpty(
    raw.customerName,
    raw.clientName,
    raw.name,
    raw.customer?.name,
    raw.customerSnapshot?.name
  ), "Cliente");

  const email = clean(firstNonEmpty(
    raw.customerEmail,
    raw.clientEmail,
    raw.email,
    raw.customer?.email,
    raw.customerSnapshot?.email
  )).toLowerCase();

  const phone = clean(firstNonEmpty(
    raw.customerPhone,
    raw.clientPhone,
    raw.phone,
    raw.customer?.phone,
    raw.customerSnapshot?.phone
  ));

  const snapshot = raw.customerSnapshot && typeof raw.customerSnapshot === "object"
    ? { ...raw.customerSnapshot, id: customerId || raw.customerSnapshot.id || raw.customerSnapshot.customerId || null, name, email: email || raw.customerSnapshot.email || null, phone: phone || raw.customerSnapshot.phone || null }
    : { id: customerId || null, name, email: email || null, phone: phone || null, source: raw.bookingSource || raw.source || "booking" };

  return {
    customerId: customerId || null,
    clientId: customerId || raw.clientId || null,
    customerName: name,
    customerEmail: email || null,
    customerPhone: phone || null,
    customerSnapshot: snapshot,
    hasCustomerIdentity: Boolean(customerId || email || phone || name),
  };
}

export function resolveBookingPaidAmount(raw = {}) {
  const lifecycle = bookingLifecycleFromRecord(raw);
  const candidates = [
    lifecycle.serviceCollectedAmount,
    lifecycle.paidAmount,
    lifecycle.amountCharged,
    raw.amountCharged,
    raw.paidAmount,
    raw.paymentAmount,
    raw.serviceCollectedAmount,
    raw.payment?.amountCharged,
    raw.payment?.paidAmount,
    raw.payment?.serviceCollectedAmount,
    raw.totalCollectedAmount,
    raw.totalPrice,
    raw.total,
    raw.price,
  ];
  for (const value of candidates) {
    const n = safeNumber(value, 0);
    if (n > 0) return n;
  }
  return 0;
}

export function shouldBookingTriggerLoyalty(raw = {}) {
  const lifecycle = bookingLifecycleFromRecord(raw);
  const status = normalizeBookingStatus(lifecycle.bookingStatus || raw.status || raw.confirmationStatus);
  const paymentStatus = normalizePaymentStatus(lifecycle.paymentStatus || raw.paymentStatus || raw.payment?.status, {
    collected: lifecycle.serviceCollectedAmount || raw.amountCharged || raw.paidAmount || 0,
    expected: lifecycle.expectedServiceAmount || raw.totalPrice || raw.total || 0,
  });
  if (["cancelled", "rejected", "no_show"].includes(status)) return false;
  return paymentStatus === "paid" && resolveBookingPaidAmount({ ...raw, ...lifecycle }) > 0;
}

export function buildCanonicalBookingPatch(raw = {}, patch = {}, options = {}) {
  const merged = { ...(raw || {}), ...(patch || {}) };
  const identity = resolveBookingCustomerIdentity(merged);
  const lifecyclePatch = buildBookingLifecyclePatch(merged, patch);
  const lifecycle = bookingLifecycleFromRecord({ ...merged, ...lifecyclePatch });

  const start = safeDate(firstNonEmpty(merged.startAt, merged.scheduledAt, merged.start, merged.dateTime));
  const end = safeDate(firstNonEmpty(merged.endAt, merged.end));
  const duration = Math.max(15, safeNumber(merged.durationMinutes ?? merged.duration ?? merged.minutes, start && end ? Math.round((end.getTime() - start.getTime()) / 60000) : 60));

  const source = clean(firstNonEmpty(merged.bookingSource, merged.source), options.defaultBookingSource || "admin_manual");
  const nowIso = new Date().toISOString();

  return {
    ...identity,
    ...lifecyclePatch,
    status: lifecycle.bookingStatus,
    bookingStatus: lifecycle.bookingStatus,
    confirmationStatus: lifecycle.confirmationStatus,
    paymentStatus: lifecycle.paymentStatus,
    paymentMethod: lifecycle.paymentMethod,
    amountCharged: lifecycle.serviceCollectedAmount,
    paidAmount: lifecycle.serviceCollectedAmount,
    paymentAmount: lifecycle.serviceCollectedAmount,
    serviceCollectedAmount: lifecycle.serviceCollectedAmount,
    coveredAmount: lifecycle.coveredAmount,
    paymentCoverageType: lifecycle.paymentCoverageType,
    commissionBaseAmount: lifecycle.commissionBaseAmount,
    expectedServiceAmount: lifecycle.expectedServiceAmount,
    serviceExpectedAmount: lifecycle.serviceExpectedAmount,
    totalDueAmount: lifecycle.totalDueAmount,
    duration,
    durationMinutes: duration,
    date: start ? dateInputValue(start) : (merged.date || null),
    time: start ? timeInputValue(start) : (merged.time || null),
    bookingSource: source,
    source,
    lifecycleReadyForLoyalty: shouldBookingTriggerLoyalty({ ...merged, ...lifecyclePatch, ...identity }),
    lifecycleUpdatedAtClient: nowIso,
    bookingEngineVersion: "R0.2",
    bookingEngineTouchedAtClient: nowIso,
  };
}

export function buildBookingSchedulePatch({ start, end, durationMinutes, employee = null, resourceId = null } = {}) {
  const startDate = safeDate(start);
  const endDate = safeDate(end);
  const duration = Math.max(15, safeNumber(durationMinutes, startDate && endDate ? Math.round((endDate.getTime() - startDate.getTime()) / 60000) : 60));
  const patch = {
    duration,
    durationMinutes: duration,
    durationMins: duration,
    minutes: duration,
    date: dateInputValue(startDate),
    time: timeInputValue(startDate),
    scheduleUpdatedAtClient: new Date().toISOString(),
    bookingEngineVersion: "R0.2",
  };
  if (resourceId || employee) {
    const id = resourceId || employee?.id || employee?.employeeId || null;
    patch.employeeId = id ? String(id) : null;
    patch.workerId = id ? String(id) : null;
    patch.resourceId = id ? String(id) : null;
    patch.employeeEmail = employee?.email || employee?.employeeEmail || employee?.workerEmail || null;
    patch.employeeName = employee?.name || employee?.displayName || employee?.employeeName || null;
    patch.workerName = patch.employeeName;
  }
  return patch;
}

export function buildBookingPaymentPatch(raw = {}, patch = {}) {
  return buildCanonicalBookingPatch(raw, patch, { defaultBookingSource: raw.bookingSource || "payment_update" });
}

export function buildBookingStatusPatch(raw = {}, nextStatus) {
  return buildCanonicalBookingPatch(raw, { status: nextStatus, confirmationStatus: nextStatus }, { defaultBookingSource: raw.bookingSource || "status_update" });
}
